import axios from "axios";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Spin, Modal, Tooltip } from "antd";
import { Mail, Lock, Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { NumberParam, useQueryParams } from "use-query-params";

import Globais, { validateToken } from "../../components/Globais";
import { useMessage } from "../../uteis/mensagens/useMessage";
import { useInteressadoAnuncio } from "./hooks/useInteressadoAnuncio";

const Container = styled.div`
  height: 100dvh; /* usa a altura dinâmica real do viewport */
  min-height: -webkit-fill-available; /* fallback para Safari iOS */
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background: linear-gradient(135deg, #003644, #0096c7);
  padding: 6rem 2rem 2rem 2rem;
  overflow: hidden; /* remove rolagem interna */

  @media (max-width: 768px) {
    height: 100dvh; /* garante a cobertura total no mobile */
    padding: 3.5rem 1rem 1rem 1rem;
  }
`;
const FormWrapper = styled.div`
  background: #fff;
  width: 100%;
  max-width: 450px;
  border-radius: 16px;
  padding: 2rem 2.5rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  animation: slideDownFade 0.6s ease forwards;

  @keyframes slideDownFade {
    from {
      opacity: 0;
      transform: translateY(-30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Title = styled.h2`
  text-align: center;
  color: #003644;
  font-weight: 600;
  margin-bottom: 1.5rem;
`;

const InputGroup = styled.div`
  position: relative;
  margin-bottom: 1.1rem;

  svg {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    color: #777;
  }

  .icon-left {
    left: 12px;
  }

  .icon-right {
    right: 12px;
    cursor: pointer;
    transition: color 0.2s;
    &:hover {
      color: #0096c7;
    }
  }

  input {
    width: 100%;
    padding: 0.8rem 2.5rem 0.8rem 2.5rem;
    border: 1px solid #ccc;
    border-radius: 8px;
    font-size: 1rem;
    outline: none;
    transition: border 0.2s, box-shadow 0.2s;

    &:focus {
      border-color: #0096c7;
      box-shadow: 0 0 0 2px rgba(0, 150, 199, 0.2);
    }
  }
`;

const Botao = styled.button`
  width: 100%;
  background: #0096c7;
  color: white;
  font-weight: 600;
  font-size: 1rem;
  padding: 0.9rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  margin-top: 0.6rem;
  transition: background 0.3s;

  &:hover {
    background: #00b4d8;
  }

  &:disabled {
    background: #90e0ef;
    cursor: not-allowed;
  }
`;

const Links = styled.div`
  text-align: center;
  font-size: 0.9rem;
  margin-top: 1.5rem;

  a {
    color: #0096c7;
    text-decoration: none;
    font-weight: 500;
  }

  a:hover {
    text-decoration: underline;
  }
`;


export default function ViewLogin() {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [isLoading, setIsLoading] = useState(false);
   const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const [param] = useQueryParams({ id: NumberParam });
  const [query] = useQueryParams({ anuncio: NumberParam });
  const anuncio = query.anuncio;
  const { cadastrar } = useInteressadoAnuncio();
  const { showSuccess, showError, showWarning, contextHolder } = useMessage();
  const [linkCadastroPessoais, setLinkCadastroPessoais] =
      useState<string>("/cadastroPessoais");


    // 🔍 Valida token no carregamento da página
  useEffect(() => {
    const inicializaPagina = () => {
      const tokenValido = validateToken();

      // Caso o usuário venha de um link com parâmetros (ex: anúncio)
      if (param.id && anuncio) return;

      // Se token ainda é válido, redireciona
      if (tokenValido) navigate("/inicio", { replace: true });
    };

    inicializaPagina();
  }, [param.id, anuncio, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.id]: e.target.value }));
  };

  // Extrai os minutos da mensagem "Aguarde X minuto(s)..."
const extrairMinutos = (texto: string): number => {
  const match = texto.match(/(\d+)/);
  return match ? Number(match[1]) : 0;
};

// Formata o contador MM:SS
const formatSeconds = (total: number): string => {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

// Reenvia o e-mail (usado após o contador zerar)
const reenviarEmail = async (email: string) => {
  try {
    await axios.post(`${Globais.DOMINIOBACK}/auth/resend-activation`, { email });
    showSuccess("Novo e-mail de ativação enviado!");
  } catch (e) {
    showError("Erro ao reenviar e-mail.");
  }
};

const abrirModalBloqueio = (minutosInicial: number, email: string) => {

  // Salva o bloqueio no localStorage
  saveResendLock(email, minutosInicial);

  let unlockAt = Date.now() + minutosInicial * 60 * 1000;

  const calcRemainingSeconds = () => {
    const s = Math.floor((unlockAt - Date.now()) / 1000);
    return s > 0 ? s : 0;
  };

  let totalSeconds = calcRemainingSeconds();

  const modal = Modal.info({
    title: "Aguarde para reenviar",
    content: `Você poderá reenviar o e-mail em ${formatSeconds(totalSeconds)}.`,
    okText: formatSeconds(totalSeconds),
    okButtonProps: { disabled: true }
  });

  const interval = setInterval(() => {
    totalSeconds = calcRemainingSeconds();

    modal.update({
      content: `Você poderá reenviar o e-mail em ${formatSeconds(totalSeconds)}.`,
      okText: formatSeconds(totalSeconds),
    });

    if (totalSeconds <= 0) {
      clearInterval(interval);

      clearResendLock(); // ⬅ desbloqueia

      modal.update({
        content: "Agora você pode reenviar o e-mail.",
        okText: "Reenviar agora",
        okButtonProps: { disabled: false },
        onOk: () => reenviarEmail(email),
      });
    }
  }, 1000);
};
const abrirModalComUnlockAt = (unlockAt: number, email: string) => {
  const calcSeconds = () =>
    Math.max(0, Math.floor((unlockAt - Date.now()) / 1000));

  let remainingSeconds = calcSeconds();

  const modal = Modal.confirm({
    title: "Aguarde para reenviar",
    content: `Você poderá reenviar em ${formatSeconds(remainingSeconds)}.`,
    okText: formatSeconds(remainingSeconds),
    cancelText: "Cancelar",
    okButtonProps: { disabled: true },
    onCancel: () => {
      // Apenas fecha, mas NÃO remove o bloqueio
      // O contador continua funcionando internamente
    },
  });

  const interval = setInterval(() => {
    remainingSeconds = calcSeconds();

    modal.update({
      content: `Você poderá reenviar em ${formatSeconds(remainingSeconds)}.`,
      okText: formatSeconds(remainingSeconds),
    });

    if (remainingSeconds <= 0) {
      clearInterval(interval);
      clearResendLock();

      modal.update({
        content: "Agora você pode reenviar o e-mail.",
        okText: "Reenviar agora",
        okButtonProps: { disabled: false },
        onOk: () => reenviarEmail(email),
      });
    }
  }, 1000);
};



// Salva no localStorage o horário em que o reenvio será liberado
const saveResendLock = (email: string, minutes: number) => {
  const unlockAt = Date.now() + minutes * 60 * 1000;

  localStorage.setItem("activationResendUnlock", JSON.stringify({
    email,
    unlockAt,
  }));
};

// Lê o registro
const getResendLock = () => {
  try {
    const data = localStorage.getItem("activationResendUnlock");
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
};

// Remove quando o tempo expira
const clearResendLock = () => {
  localStorage.removeItem("activationResendUnlock");
};




  const executarLogin = async () => {
    setIsLoading(true);

    const URL = `${Globais.DOMINIOBACK}/auth/login`;
    
    try {

      if (!window.navigator.onLine) {
        showWarning("Sem conexão com a internet!");
        return;
      }

      const response = await axios.post(URL, formData);
      const { accessToken, temCotasAtivas } = response.data;

      if (!accessToken) throw new Error("Token não recebido.");

          // 🔐 Armazena token e decodifica
        localStorage.setItem("accessToken", accessToken);
      const Token = validateToken();

      if (!Token) {
        showError("Erro ao validar sessão. Tente novamente.");
        return;
}

      // Administradores e operadores nao usam o fluxo de cadastro de cotas.
      if (Token.nda === 1 || Token.nda === 2) {
        showSuccess("Autenticado com sucesso!");
        setTimeout(() => navigate("/inicio", { replace: true }), 1800);
        return;
      }
        // 🧭 Caso usuário tenha vindo de um anúncio
    if (param.id && anuncio) {
      const expirationTime = new Date().getTime() + 60 * 60 * 1000; // 1h
      const URLCTU = `${Globais.DOMINIOBACK}/cota-usuario/${param.id}`;
      const URLContato = `${Globais.DOMINIOBACK}/usuario/contato`;

      try {
        // 1️⃣ Obtém dados do proprietário da cota
        const responseCTU = await axios.get(URLCTU, {
          headers: { Authorization: Token.AuthStr },
        });

        if (responseCTU?.data?.usuarioId) {
          const formContato = { id: responseCTU.data.usuarioId };
          const responseContato = await axios.post(URLContato, formContato, {
            headers: { Authorization: Token.AuthStr },
          });

          // 2️⃣ Busca o telefone do anúncio
          const responseCelular = await axios.get(
            `${Globais.DOMINIOBACK}/anuncio-propriedade/telefone/${anuncio}`
          );

          // 3️⃣ Registra interesse
          const interessadoData = {
            idAnuncio: anuncio,
            idUsuario: Token.id,
          };
          await cadastrar(interessadoData);

          // 4️⃣ Sucesso → redireciona
          showSuccess("✅ Autenticado com sucesso!");
          setTimeout(() => {
            document.location.href = `${Globais.DOMINIOANUNCIO}/detalhes.html?id=${param.id}&params=${responseCelular.data.CotasUsuariosTable.UsuarioTable.celular}&expires=${expirationTime}`;
          }, 1800);
        }
      } catch (error) {
        console.error("Erro ao registrar interesse:", error);
        showError("Erro ao registrar interesse. Tente novamente.");
      }
    } else {
      // 🔁 Fluxo normal de login (sem anúncio)
      try {
        if (temCotasAtivas === false) {
          showSuccess("✅ Autenticado com sucesso!");
          setTimeout(() => {
            navigate("/cadastroPropriedade/v2", { replace: true });
          }, 1800);
        } else {
          showSuccess("✅ Autenticado com sucesso!");
          setTimeout(() => navigate("/inicio", { replace: true }), 1800);
        }
      } catch (error) {
        console.error("Erro ao obter usuário:", error);
        showError("Erro ao carregar dados do usuário.");
      }
    }
     

    } catch (err: any) {
      const code = err.response?.data?.code;
      const msg = err.response?.data?.error;

      switch (code) {
        case "USUARIO_NAO_ENCONTRADO":
        case "SENHA_INVALIDA":
          showError("Usuário ou senha inválidos!");
          break;
        
            case "CONTA_NAO_ATIVADA":
               // Antes de abrir o modal → verifica se já existe bloqueio
             const lock = getResendLock();
                if (lock) {
                  const remaining = lock.unlockAt - Date.now();

                  if (remaining > 0) {
                    abrirModalComUnlockAt(lock.unlockAt, lock.email);
                    return; // impede reenvio mesmo que usuário tente
                  } else {
                    clearResendLock();
                  }
                }
            Modal.confirm({
              title: "Conta não ativada",
              content: "Sua conta ainda não foi ativada. Deseja reenviar o e-mail de ativação?",
              okText: "Reenviar e-mail",
              cancelText: "Cancelar",
              onOk: async () => {
                try {
                  await axios.post(`${Globais.DOMINIOBACK}/auth/resend-activation`, {
                    email: formData.email,
                  });

                  showSuccess("Novo e-mail de ativação enviado!");
                  return true; // força o fechamento do modal
                } catch (err2: any) {
                  const code2 = err2.response?.data?.code;
                  const msg2 = err2.response?.data?.error;

                  if (code2 === "REENVIO_BLOQUEADO") {
                    const minutos = extrairMinutos(msg2);
                      const unlockAt = Date.now() + minutos * 60000;

                      saveResendLock(formData.email, minutos); // mantém para persistência

                      abrirModalComUnlockAt(unlockAt, formData.email);
                      return true;
                  } else {
                    showError(msg2 || "Erro ao reenviar o e-mail.");
                  }

                 
                }
              },
            });
            break;
            case "REENVIO_BLOQUEADO":
        // Caso o usuário encontre esse erro sem passar pelo modal
        const minutos = extrairMinutos(msg);
        abrirModalBloqueio(minutos, formData.email);
        break;
        default:
          showError(msg || "❌ Ocorreu um erro ao efetuar o login. Tente novamente.");
      }
    } finally {
      setIsLoading(false);
      
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (Globais.TITULO === "HOMOLOGAÇÃO") {
      Modal.confirm({
        title: "Ambiente de Testes",
        content:
          "Você está acessando o ambiente de *homologação*. Deseja continuar?",
        okText: "Continuar aqui",
        cancelText: "Ir para o ambiente real",
        onOk: executarLogin,
        onCancel: () => {
          window.location.href = "https://proprietario.multicotas.com.br/login";
        },
      });
    } else {
      executarLogin();
    }
  };

  return (
    <Container>
    {contextHolder}
      <FormWrapper>
        <form onSubmit={handleSubmit}>
          <Title>Entrar</Title>

          <InputGroup>
            <Mail size={18} className="icon-left" />
            <input
              type="email"
              id="email"
              required
              placeholder="Digite seu e-mail"
              value={formData.email}
              onChange={handleChange}
            />
          </InputGroup>

           <InputGroup>
            <Tooltip
              title="A senha deve conter letras maiúsculas e minúsculas, ao menos um número e 8 caracteres."
              placement="topRight"
              color="#0096c7"
            >
              <span>
                <Lock size={18} className="icon-left" />
              </span>
            </Tooltip>

            <input
              type={showPassword ? "text" : "password"}
              id="password"
              required
              placeholder="Digite sua senha"
              value={formData.password}
              onChange={handleChange}
            />

            {showPassword ? (
              <EyeOff
                size={18}
                className="icon-right"
                onClick={() => setShowPassword(false)}
              />
            ) : (
              <Eye
                size={18}
                className="icon-right"
                onClick={() => setShowPassword(true)}
              />
            )}
          </InputGroup>


          <Botao type="submit" disabled={isLoading}>
            {isLoading ? <Spin size="small" /> : "Entrar"}
          </Botao>

        

          <Links>
            <a href="/esqueceusenha">
              Esqueceu sua senha?
            </a>
            <br />
            Ainda não tem uma conta?{" "}
            <a href="/cadastroPessoais">
              Cadastre-se
            </a>
          </Links>
        </form>
      </FormWrapper>
    </Container>
  );
}
